import { LoadingButton } from '@mui/lab';
import { Alert, Link, Stack, Typography } from '@mui/material';
import { ForgotConfirmForm, ForgotForm, ForgotPasswordForm } from 'app/pages/Forgot';
import { useAsyncAction } from 'app/services';
import { sessionConfirmResetPassword, sessionResetPassword, sessionSetResetPassword } from 'app/store/session';
import React, { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

const Forgot = () => {
  const formRef = useRef();
  const [step, setStep] = useState('email');

  const {
    asyncAction: resetPasswordSubmit,
    isLoading: isResetPasswordLoading,
    isSuccessful: isResetPasswordSuccessful,
    isError: isResetPasswordError,
  } = useAsyncAction(sessionResetPassword, [step]);
  const {
    asyncAction: confirmPasswordSubmit,
    isLoading: isConfirmPasswordLoading,
    isSuccessful: isConfirmPasswordSuccessful,
    isError: isConfirmPasswordError,
  } = useAsyncAction(sessionConfirmResetPassword, [step]);
  const {
    asyncAction: setPasswordSubmit,
    isLoading: isSetPasswordLoading,
    isSuccessful: isSetPasswordSuccessful,
    isError: isSetPasswordError,
  } = useAsyncAction(sessionSetResetPassword, [step]);

  const handleClickForm = () => {
    formRef.current?.handleSubmitForm();
  };

  const handleSubmitEmailForm = (params) => {
    try {
      resetPasswordSubmit(params.email);
      setStep('code');
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmitCodeForm = (params) => {
    try {
      confirmPasswordSubmit(params.code);
      setStep('password');
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmitResetPasswordForm = (params) => {
    setPasswordSubmit(params.password);
  };

  return (
    <React.Fragment>
      <Stack
        direction='row'
        flexDirection='column'
        sx={(theme) => ({
          paddingBottom: theme.spacing(2),
          '.MuiTypography-h5': {
            marginBottom: theme.spacing(1),
          },
          '.MuiTypography-body2': {
            color: theme.palette.text.secondary,
          },
          ...((isSetPasswordSuccessful || isConfirmPasswordSuccessful || isResetPasswordSuccessful) && {
            alignItems: 'center',
            paddingBottom: theme.spacing(0),
            '.MuiTypography-root': {
              textAlign: 'center',
            },
          }),
        })}>
        {step === 'email' && (
          <React.Fragment>
            <Typography variant='h5'>Восстановление доступа</Typography>
            <Typography variant='body2'>
              Введите свой электронный адрес, и мы отправим вам ссылку для восстановления доступа к аккаунту.
            </Typography>
          </React.Fragment>
        )}
        {step === 'code' && (
          <React.Fragment>
            <Typography variant='h5'>Введите код подтверждения</Typography>
            <Typography variant='body2'>Введите код проверки, который был выслан на указанную вами почту.</Typography>
          </React.Fragment>
        )}
        {step === 'password' && isSetPasswordSuccessful && (
          <React.Fragment>
            <Typography variant='h5'>Пароль успешно изменен</Typography>
            <Typography variant='body2'>
              Ваш пароль был успешно изменен. Теперь вы можете использовать новые сведения для входа в свой аккаунт.
            </Typography>
          </React.Fragment>
        )}
        {step === 'password' && !isSetPasswordSuccessful && (
          <React.Fragment>
            <Typography variant='h5'>Введите новый пароль</Typography>
            <Typography variant='body2'>Введите новый пароль для доступа к аккаунту. Пароль должен быть длиннее 6 символов.</Typography>
          </React.Fragment>
        )}
      </Stack>
      {step === 'email' && <ForgotForm ref={formRef} onSubmit={handleSubmitEmailForm} />}
      {step === 'code' && <ForgotConfirmForm ref={formRef} onSubmit={handleSubmitCodeForm} />}
      {step === 'password' && !isSetPasswordSuccessful && <ForgotPasswordForm ref={formRef} onSubmit={handleSubmitResetPasswordForm} />}
      <Stack
        direction='row'
        justifyContent='flex-end'
        sx={(theme) => ({
          width: '100%',
          '.MuiAlert-root': {
            width: '100%',
            marginTop: theme.spacing(2),
            borderRadius: theme.shape.borderRadius,
          },
        })}>
        {isResetPasswordError && <Alert severity='error'>Указанный вами аккаунт не найден. Проверьте правильность введенных данных.</Alert>}
        {isConfirmPasswordError && (
          <Alert severity='error'>Код подтверждения указан не верно. Проверьте правильность введенных данных.</Alert>
        )}
        {isSetPasswordError && (
          <Alert severity='error'>Произошла ошибка при изменении пароля. Попробуйте снова или обратитесь в техническую поддержку.</Alert>
        )}
      </Stack>
      <Stack
        direction='column'
        alignItems='center'
        justifyContent='flex-start'
        spacing={2}
        sx={(theme) => ({
          marginTop: theme.spacing(3),
          '.MuiLoadingButton-root': {
            lineHeight: '22px',
            padding: theme.spacing(2),
          },
        })}>
        {step === 'email' && !isResetPasswordSuccessful && (
          <LoadingButton variant='contained' loading={isResetPasswordLoading} onClick={handleClickForm} fullWidth disableElevation>
            Продолжить
          </LoadingButton>
        )}
        {step === 'code' && !isConfirmPasswordSuccessful && (
          <LoadingButton variant='contained' loading={isConfirmPasswordLoading} onClick={handleClickForm} fullWidth disableElevation>
            Подтвердить
          </LoadingButton>
        )}
        {step === 'password' && !isSetPasswordSuccessful && (
          <LoadingButton variant='contained' loading={isSetPasswordLoading} onClick={handleClickForm} fullWidth disableElevation>
            Подтвердить
          </LoadingButton>
        )}
        {(isSetPasswordSuccessful || isResetPasswordSuccessful || isConfirmPasswordSuccessful) && (
          <Link to='/' variant='subtitle2' underline='none' component={RouterLink} sx={{ width: '100%' }}>
            <LoadingButton variant='contained' fullWidth disableElevation>
              Вернуться к входу
            </LoadingButton>
          </Link>
        )}
        {(step !== 'password' || !isConfirmPasswordSuccessful || !isResetPasswordSuccessful) && (
          <Typography variant='body2'>
            <Link to='/' variant='subtitle2' underline='none' component={RouterLink}>
              Вернуться к входу
            </Link>
          </Typography>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default Forgot;
